import Commons from "oc-commons/Commons.js";
import {v3} from "./short3.js";
import Maths3 from "oc-threejs/Maths3.js";
import Cone from "./math/Cone.js";
import Scenes from "./Scenes.js";
import ConeIntersectionHandler from "./collision/ConeIntersectionHandler.js";


/**
 * Statische Utility-Klasse zu Mouse und Screen, Umrechnung der Koordinaten und sichtbare Größen,
 * Mouse-Picking (speziell Raycasting für Maus)
 *
 * Eventuell ist ScreenUtils besser geeignet. ScreenUtils ist eine neuere Implementierung
 * und stammt aus dem Easy-Zaunplaner.
 */
export default class Screens {

    /**
     * gibt die aktuell sichtbare Größe einer Kugel in Pixeln zurück
     */
    static visibleSizeInPx(sphere) {
        return Screens.visibleSizeInPxCalculator()(sphere.center, sphere.radius);
    }

    /**
     * Gibt eine Funktion (center, radius = 1) => Pixel-Anzahl zürück
     */
    static visibleSizeInPxCalculator() {
        const vsf = Screens.visibleSizeFactor();
        return (center, radius = 1) => vsf * radius / STAGE.camera.position.distanceTo(center);
    }

    /**
     * gibt das Größenverhältnis Canvas-Höhe zu Camera-Fov zurück
     */
    static visibleSizeFactor() {
        return Math.max(STAGE.canvas.height(),1) / (2 * Math.tan(STAGE.camera.fov * Math.PI / 360));
    }

    /**
     * ist eine World - Position sichtbar?
     */
    static isVisiblePosition(position,min=0,max=1) {
        try {
            const screenPosition = position.clone().project(STAGE.camera);
            const x = (screenPosition.x + 1) / 2;
            const y = -(screenPosition.y - 1) / 2;
            return min<x && x<max && min<y && y<max;
        } catch (e) {
            return true;
        }
    }

    /**
     * gibt die Pixelkoordinaten auf dem Screen eines Punktes im 3D-Raum zurück
     */
    static screenPosition(position) {
        const screenPosition = position.clone().project(STAGE.camera);
        screenPosition.x = (screenPosition.x + 1) / 2 * STAGE.canvas.width();
        screenPosition.y = -(screenPosition.y - 1) / 2 * STAGE.canvas.height();
        return screenPosition;
    }

    /**
     * gibt die Maus-Koordinaten jeweils als Verhältnis des Canvas zurück, d.h. -1 (links/oben) bis 1 (unten/rechts)
     */
    static getMouseInDeviceCoords(event) {
        const canvas = STAGE.canvas;
        const canvasOffset = canvas.offset();

        const x = event.pageX - canvasOffset.left;
        const y = event.pageY - canvasOffset.top;

        const mouse = v3();
        mouse.x = ( x / canvas.width() ) * 2 - 1;
        mouse.y = -( y / canvas.height() ) * 2 + 1;

        return mouse;
    }

    /**
     * Rechnet die Mauskoordinaten in Weltkoordinaten um, wobei der z-Wert vorgegeben wird
     */
    static mouseToWorldCoordinates(event, z = 100) {
        const camera = STAGE.camera;
        const testPoint = camera.position.clone().add(camera.getWorldDirection(v3()).multiplyScalar(z));
        testPoint.project(camera);
        const mouse = Screens.getMouseInDeviceCoords(event);
        mouse.z = testPoint.z;
        mouse.unproject(camera);
        return mouse;
    }
    /**
     * Rechnet die 2DKoordinaten relativ zum Canvas (canvas oben links=0,0)in Weltkoordinaten um, wobei der z-Wert vorgegeben wird
     */
    static v2ToWorldCoordinates(position,z=100){
        const camera = STAGE.camera;
        const testPoint = camera.position.clone().add(camera.getWorldDirection(v3()).multiplyScalar(z));
        testPoint.project(camera);

        const canvas = STAGE.canvas;

        const x = position.x;
        const y = position.y;

        const result = v3();
        result.x = ( x / canvas.width() ) * 2 - 1;
        result.y = -( y / canvas.height() ) * 2 + 1;
        result.z = testPoint.z;
        result.unproject(camera);
        return result;
    }

    /**
     * Führt einen Raycast durch, wobei zuerst ein exakter Ray getestet wird, und wenn der nichts findet,
     * mit einem Volumentest (Kegel, der etwa dem Sehbereich unter der Berührposition eines Fingers auf einem
     * Touchdevice entspricht). Dadurch können auch feine Objekte wie Gitter auf einem Touch-Device gut
     * selektiert werden.
     * Über die Eigenschaft intersectionPriority können auch eigentlich durch andere Objekte verdeckte
     * Objekte zurückgegeben werden.
     * @returns das naheste gefundene Objekt mit der höchsten Priorität oder null
     */
    static fetchObjectAtMouse(event, selectableMeshs) {
        const intersection = Screens.intersectionAtMouse(event, selectableMeshs);
        return intersection?.object;
    }

    /**
     * Führt einen Raycast durch, wobei zuerst ein exakter Ray getestet wird, und wenn der nichts findet,
     * mit einem Volumentest (Kegel, der etwa dem Sehbereich unter der Berührposition eines Fingers auf einem
     * Touchdevice entspricht). Dadurch können auch feine Objekte wie Gitter auf einem Touch-Device gut
     * selektiert werden.
     * Über die Eigenschaft intersectionPriority können auch eigentlich durch andere Objekte verdeckte
     * Objekte zurückgegeben werden.
     * @returns die naheste Intersection mit der höchsten Priorität oder null
     * @example
     * //Das Schnitt-Ergebniss liegt in der folgenden Form vor:
     * {
     *      point, // der Schnittpunkt (geht immer durch die Kegel-Achse)
     *      distance, // der Abstand point zum Kegel.origin (die Spitze)
     *      object, // das geschnittene Objekt
     *      face, // das Dreieck
     *      faceIndex, // Index des Dreiecks
     * }
     */
    static intersectionAtMouse(event, selectableMeshs) {
        if (Commons.isEmpty(selectableMeshs)) {
            return null;
        }
        // probiere einen exakten Treffer auf dem Sehstrahl
        const first = Screens.rayCastAtMouse(event, selectableMeshs);
        if (first) {
            return first;
        }
        //Fallback für Cone Test
        const mouse = Screens.getMouseInDeviceCoords(event);
        const cone = new Cone().setFromCameraForTouch(mouse, 3);
        let minPriority = 0;
        let minIntersection = {
            distance: Number.POSITIVE_INFINITY,
        }
        new ConeIntersectionHandler(selectableMeshs,{cone}).intersect((intersection)=> {
            const priority = Screens.getIntersectionPriorityUp(intersection.object);
            if (priority > minPriority || (priority == minPriority && minIntersection.distance>intersection.distance)) {
                minIntersection = intersection;
                minPriority = priority;
            }
        });
        if (!Number.isFinite(minIntersection.distance)) {
            return null;
        }

        return minIntersection;
    }

    /**
     * führt einen normalen RayCast durch, der aber nicht besonders für Touch-Devices geeignet ist,
     * da lieber fetchObjectAtMouse nehmen.
     */
    static rayCastAtMouse(event, selectableMeshs, returnAllMatches=false) {
        if (Commons.isEmpty(selectableMeshs)) {
            return;
        }
        const mouse = Screens.getMouseInDeviceCoords(event);

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, STAGE.camera);
        const intersects = raycaster.intersectObjects(selectableMeshs, true);

        intersects.sort((a,b) => {
            const aPrio = Screens.getIntersectionPriorityUp(a.object);
            const bPrio = Screens.getIntersectionPriorityUp(b.object);
            const n = bPrio - aPrio;
            return n !== 0 ? n : (a.distance - b.distance);
        });

        if (returnAllMatches) {
            return intersects;
        }

        // const first = intersects.find(o=>!(o.object instanceof THREE.Sprite));
        const first = _.first(intersects);

        if (first == null) {
            return null;
        }

        first.ray = raycaster.ray;

        return first;
    }

    /**
     * Gibt den nächsten Punkt auf dem Strahl durch die Maus zurück zu einer gegebenen Plane
     */
    static rayCastAtMouseOnPlane(event, plane, offset = Maths3.vector.zero) {
        const ray = Screens.getRayFromMouse(event);
        ray.origin.sub(offset);
        const rayMeetsPlane = ray.intersectPlane(plane);
        return rayMeetsPlane;
    }

    /**
     * Gibt den nächsten Punkt auf dem Strahl durch die Maus zurück zu einer gegebenen Linie
     */
    static rayCastAtMouseOnLine(event, line, offset = Maths3.vector.zero) {
        const ray = Screens.getRayFromMouse(event);
        ray.origin.sub(offset);
        const pointOnMouseRay = v3();
        const pointOnRay = v3();
        ray.distanceSqToSegment(line.start, line.end, pointOnMouseRay, pointOnRay);
        return [pointOnRay, pointOnMouseRay];
    }

    /**
     * Gibt den nächsten Punkt auf dem Strahl durch die Maus zurück zu einem gegebenen Punkt
     */
    static rayCastAtMouseToPoint(event, point, offset = Maths3.vector.zero) {
        const ray = Screens.getRayFromMouse(event);
        ray.origin.sub(offset);
        const position = ray.closestPointToPoint(point);
        position.add(offset);
        return position;
    }


    /**
     * @returns {*|Debugs.ray}
     */
    static getRayFromMouse(event) {
        const raycaster = new THREE.Raycaster();
        const mouse = Screens.getMouseInDeviceCoords(event);
        raycaster.setFromCamera(mouse, STAGE.camera);
        return raycaster.ray;
    }

    /**
     * gibt die intersectionPriority des Objektes bzw. des ersten Parents mit intersectionPriority zurück, oder 0
     * oder -1 wenn object nicht visible
     */
    static getIntersectionPriorityUp(object) {
        const intersectionPriorityHolder = Scenes.findUp(object, o=>o._intersectionPriority!=null);
        return intersectionPriorityHolder ? intersectionPriorityHolder.intersectionPriority : 0;
    }

    /**
     * Schickt eine Reihe von Rays in die Szene, entsprechend dem gerasterten Kugelschatten des Objektes (Bounding Sphere),
     * und gibt die Schnittpunkte mit der Szene (oder der Grund-Plane) zurück.
     */
    static rayCastAtMouseCircleRaster(event, selectableMeshs, radiusInWorld, numberOfRays = 10, plane) {
        const mouse = Screens.getMouseInDeviceCoords(event);
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, STAGE.camera);
        return Screens.rayCastCircleRaster(raycaster.ray, selectableMeshs, radiusInWorld, numberOfRays, plane);
    }

    /**
     * Schickt eine Reihe von Rays in die Szene, entsprechend dem gerasterten Kugelschatten des Objektes (Bounding Sphere),
     * und gibt die Schnittpunkte mit der Szene (oder der Grund-Plane) zurück.
     */
    static rayCastCircleRaster(ray, selectableMeshs, radiusInWorld, numberOfRays = 10, plane) {
        const raycaster = new THREE.Raycaster(ray.origin,ray.direction);

        const result = {
            ray,
            points: [],
        };

        Scenes.iteratePointsOnCircleRaster(ray, radiusInWorld, numberOfRays, (rasterPoint) => {
            raycaster.ray.origin = rasterPoint;
            const intersects = raycaster.intersectObjects(selectableMeshs, true);
            const first = intersects.find(o=>!(o.object instanceof THREE.Sprite));
            if (first) {
                result.points.push(first.point);
                //Utils.debugPoint(first.point)
            } else if (plane) {
                const planePoint = raycaster.ray.intersectPlane(plane);
                if (planePoint) {
                    result.points.push(planePoint);
                }
            }
        });
        return result;
    }

}
